import { useEffect, useState } from 'react'
import { computePeaks } from './audio-peaks'

export type WaveformStatus = 'idle' | 'loading' | 'ready' | 'failed'

export interface AudioPeaks {
  status: WaveformStatus
  peaks: number[] | null
  /** Seconds, from the decoded buffer; null until decoded or when decoding fails. */
  duration: number | null
}

const IDLE: AudioPeaks = { status: 'idle', peaks: null, duration: null }
const LOADING: AudioPeaks = { status: 'loading', peaks: null, duration: null }
const FAILED: AudioPeaks = { status: 'failed', peaks: null, duration: null }

/**
 * Files above this are not decoded: the decoded PCM is many times the file
 * size, and the waveform is only a picture. The node keeps playing the file.
 */
export const MAX_WAVEFORM_BYTES = 32 * 1024 * 1024

/**
 * Peaks only need the envelope, so decoding resamples to a low rate: about a
 * fifth of the memory of decoding at 44.1 kHz.
 */
const DECODE_SAMPLE_RATE = 8000

/** Waveforms by URL, so a node that remounts (React Flow drops off-screen nodes) never decodes again. */
const MAX_CACHED = 64
const cache = new Map<string, Promise<AudioPeaks>>()

/** Test seam: forget every cached waveform. */
export function clearAudioPeaksCache(): void {
  cache.clear()
}

async function decodePeaks(url: string, sizeHint: number | undefined): Promise<AudioPeaks> {
  if (sizeHint && sizeHint > MAX_WAVEFORM_BYTES) return FAILED
  const OfflineContext = window.OfflineAudioContext
  if (!OfflineContext) return FAILED
  const controller = new AbortController()
  const response = await fetch(url, { signal: controller.signal })
  if (!response.ok) return FAILED
  const length = Number(response.headers?.get?.('content-length') ?? '')
  if (length > MAX_WAVEFORM_BYTES) {
    controller.abort()
    return FAILED
  }
  const bytes = await response.arrayBuffer()
  if (bytes.byteLength > MAX_WAVEFORM_BYTES) return FAILED
  const buffer = await new OfflineContext(1, 1, DECODE_SAMPLE_RATE).decodeAudioData(bytes)
  return { status: 'ready', peaks: computePeaks(buffer), duration: buffer.duration }
}

function loadPeaks(url: string, sizeHint: number | undefined): Promise<AudioPeaks> {
  const cached = cache.get(url)
  if (cached) return cached
  // Any failure (CORS, unsupported codec, network) is a flat placeholder, cached like a success.
  const pending = decodePeaks(url, sizeHint).catch(() => FAILED)
  cache.set(url, pending)
  if (cache.size > MAX_CACHED) cache.delete(cache.keys().next().value as string)
  return pending
}

/**
 * The whole-file waveform of an audio file. Decoding runs in an
 * OfflineAudioContext, which needs no user gesture and plays nothing. Each URL
 * is decoded at most once per session, however often its node remounts.
 */
export function useAudioPeaks(url: string | null, sizeBytes?: number): AudioPeaks {
  const [result, setResult] = useState<AudioPeaks>(IDLE)

  useEffect(() => {
    if (!url) {
      setResult(IDLE)
      return
    }
    let cancelled = false
    setResult(LOADING)
    loadPeaks(url, sizeBytes).then((peaks) => {
      if (!cancelled) setResult(peaks)
    })
    return () => {
      cancelled = true
    }
  }, [url, sizeBytes])

  return result
}
